import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  colorThemeGenerator,
  Backdrop,
  ButtonPrimary,
  ButtonTertiary,
  FormGroup,
  FormCheckbox,
  DialogConfirmation,
  Type,
} from '@spotify-internal/encore-creator-web';
import {
  gray95,
  spacer12,
  spacer16,
  redLight,
  azure,
  white,
  screenSmMin,
} from '@spotify-internal/encore-foundation';
import { useMe } from '../../hooks/useMe';
import { acceptTerms } from '../../lib/Api/';
import { trackEvent } from '../../lib/Tracking';
import { FormattedMessage } from 'react-intl';

const StyledDialogConfirmation = styled(DialogConfirmation)`
  display: flex;
  @media (max-width: ${screenSmMin}) {
    align-items: center;
    text-align: center;
  }
`;

const FormAndErrorContainer = styled.div`
  width: 100%;
  margin-bottom: ${spacer12};
  @media (max-width: ${screenSmMin}) {
    text-align: center;
  }
`;

const ErrorMessage = styled(Type)`
  color: ${redLight};
  margin: ${spacer16} 0;
  display: block;
`;

const StyledFormGroup = styled(FormGroup)`
  background: ${gray95};
  padding: ${spacer12};
  text-align: left;
`;

const AzureLink = styled.a`
  color: ${azure};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: flex-end;

  & button,
  & a {
    margin-left: 0;
  }
  @media (max-width: ${screenSmMin}) {
    flex-direction: column;
    align-items: center;
    & button {
      order: -1;
    }
  }
`;

const logoutUrl = `https://accounts.spotify.com/logout?continue=${window.location.origin}`;

export const TermsAndConditions = props => {
  const me = useMe()!;
  const { needsToAcceptTerms, permissions } = me;

  const [showTerms, setShowTerms] = useState(needsToAcceptTerms);

  const [accepted, setAccepted] = useState(false);

  const [error, setError] = useState(false);

  // don't show terms to network users as they've already accepted our ToC
  const showTermsAndConditions = showTerms && !permissions.network;

  return showTermsAndConditions ? (
    <Backdrop center data-testid="terms">
      <StyledDialogConfirmation
        theme={{
          ...colorThemeGenerator({
            primaryColor: azure,
            primaryColorText: white,
            primaryColorFocusText: white,
            primaryColorActiveText: white,
          }),
        }}
        dialogTitle={
          <Type variant={Type.heading4} weight={Type.bold}>
            <FormattedMessage
              id="terms-and-conditions.title"
              defaultMessage="We updated our terms and conditions"
            />
          </Type>
        }
        body={
          <div style={{ marginBottom: spacer12 }}>
            <Type variant={Type.body2}>
              <FormattedMessage
                id="terms-and-conditions.description"
                defaultMessage="To continue using Spotify for Podcasters, please accept <link>the updated terms.</link>"
                values={{
                  link: (...chunks) => (
                    <AzureLink href="https://www.spotify.com/legal/spotify-for-podcasters-platform-terms">
                      {chunks}
                    </AzureLink>
                  ),
                }}
              />
            </Type>
          </div>
        }
        footer={
          <>
            <FormAndErrorContainer>
              <StyledFormGroup>
                <FormCheckbox
                  data-testid="terms-checkbox"
                  checked={accepted}
                  onChange={() => setAccepted(accepted => !accepted)}
                >
                  <FormattedMessage
                    id="terms-and-conditions.acceptance-text"
                    defaultMessage="I have read and agree to the terms and conditions."
                  />
                </FormCheckbox>
              </StyledFormGroup>
              {error ? (
                <ErrorMessage data-testid="terms-error">
                  <FormattedMessage
                    id="terms-and-conditions.error"
                    defaultMessage="Something went wrong. Wait a few minutes, then try again."
                  />
                </ErrorMessage>
              ) : null}
            </FormAndErrorContainer>
            <ButtonContainer>
              <ButtonTertiary
                data-testid="terms-logout"
                href={logoutUrl}
                onClick={() => trackEvent({ category: 'terms-and-conditions', action: 'logout' })}
              >
                <FormattedMessage id="terms-and-conditions.logout-button" defaultMessage="Logout" />
              </ButtonTertiary>
              <ButtonPrimary
                data-testid="terms-accept"
                onClick={async () => {
                  try {
                    await acceptTerms();
                    setShowTerms(false);
                    trackEvent({
                      category: 'terms-and-conditions',
                      action: 'accept',
                      label: 'success',
                    });
                  } catch {
                    setError(true);
                    trackEvent({
                      category: 'terms-and-conditions',
                      action: 'accept',
                      label: 'error',
                    });
                  }
                }}
                disabled={!accepted}
              >
                <FormattedMessage
                  id="terms-and-conditions.accept"
                  description="Button label to accept the terms and conditions"
                  defaultMessage="I Accept"
                />
              </ButtonPrimary>
            </ButtonContainer>
          </>
        }
      />
    </Backdrop>
  ) : (
    props.children
  );
};
